import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "@/store";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Info, MapPinOff, CalendarX2, Lock, Mail, MessageSquare, Phone, PhoneCall, Loader2, Globe, Clock } from "lucide-react";
import { flowRestarted } from "../publicBookingFlowSlice";
import { useCallUpcomingBookingDetailsMutation } from "../publicBookingFlowApi";
import { BOOKING_SEASON_CLOSED_TERMINAL, HELD_TIME_GONE_TERMINAL, PROGRAM_REFUSED_TERMINAL, SLOT_TAKEN_TERMINAL } from "../publicBookingFlowTypes";
import { BookingSeasonClosedNotice } from "./BookingSeasonClosedNotice";
import { FindAnotherTimeNotice } from "./FindAnotherTimeNotice";

function formatPhone(phone: string): string {
  return `(${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}`;
}

const MSC_PHONE_DISPLAY = "(253) 838-6810";
const MSC_PHONE_HREF = "tel:+12538386810";

/**
 * What somebody sees when the Social Security number they typed already has an upcoming appointment
 * against it, of any event type. Terminal because the booking is blocked — MSC's rule, enforced by
 * the server; see docs/booking-duplicate-appointment-block.md.
 *
 * Reached from two places: the step-2 check, which carries `noticeId` and `phoneLast4`, and the 409
 * on the booking itself, which carries neither. Both are the same dead end, so they render the same
 * screen; only the offer to call differs, and it is absent in the second case.
 *
 * The appointment is deliberately not on this screen and cannot be put here later without redoing the
 * privacy analysis behind it. The check that got us here is answerable by anyone who knows the
 * applicant's number, so rendering the date, the site or the confirmation code would turn this page
 * into a way to read a stranger's schedule out of a Social Security number. What the page can do is
 * offer to have the appointment read out to the handset already recorded on that booking — a number
 * it never learns, addressed by an opaque notice id, and shown only as its last four digits so the
 * person can tell whether it is theirs.
 *
 * `phoneLast4` being absent is a real case, not a defensive branch: the booking on file may carry no
 * number that can be called. The applicant is still stopped — they already have an appointment — and
 * is told to phone us instead.
 */
function ExistingAppointmentNotice({
  noticeId,
  phoneLast4,
  onReset,
}: {
  noticeId?: string;
  phoneLast4?: string;
  onReset: () => void;
}) {
  const [callDetails, { isLoading }] = useCallUpcomingBookingDetailsMutation();
  const [called, setCalled] = useState(false);
  const [failed, setFailed] = useState(false);

  const canCall = !!noticeId && !!phoneLast4;

  const handleCall = async () => {
    if (!noticeId) return;
    setFailed(false);
    try {
      await callDetails(noticeId).unwrap();
      setCalled(true);
    } catch {
      // One message for every reason the call did not happen — the number has opted out, it has had
      // its share of these calls, or Twilio would not take it. The server does not distinguish them
      // either, and the instruction is the same in all three: phone us.
      setFailed(true);
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto text-center space-y-4 pt-10 pb-6 px-4">
      <div className="mx-auto w-16 h-16 bg-amber-100 flex items-center justify-center rounded-full">
        <Info className="w-8 h-8 text-amber-600" />
      </div>
      <h2 className="text-2xl font-semibold text-slate-900">An Appointment Already Exists</h2>
      <p className="text-slate-600">
        Your household already has an upcoming appointment with us, so there is no need to book
        another one.
      </p>

      <div className="space-y-4 mt-6">
        <div className="bg-amber-50/50 border border-amber-200 rounded-lg p-4 flex items-start gap-3 text-left">
          <Lock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            For your security, appointment details are <strong>not shown on this screen</strong>.
            {canCall
              ? " We can call your phone number on file and read them to you."
              : " Please call us and we can go over them with you."}
          </p>
        </div>

        {canCall && !called && (
          <div className="max-w-sm mx-auto space-y-2">
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-auto min-h-11 py-3 text-left whitespace-normal"
              disabled={isLoading}
              onClick={handleCall}
            >
              {isLoading ? <Loader2 className="w-4 h-4 shrink-0 animate-spin" /> : <Phone className="w-4 h-4 shrink-0" />}
              {isLoading
                ? "Calling…"
                : `Send appointment information to your phone number on file ending in ${phoneLast4}`}
            </Button>
            <p className="text-xs text-slate-500">
              We will read out your check-in time, your appointment time and the address.
            </p>
          </div>
        )}

        {called && (
          <div className="max-w-sm mx-auto rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 text-left flex items-start gap-3">
            <PhoneCall className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-900">
              We are calling the number ending in <strong>{phoneLast4}</strong> now with your check-in
              time, appointment time and address. If you miss it, call us on{" "}
              <a className="underline" href={MSC_PHONE_HREF}>{MSC_PHONE_DISPLAY}</a>.
            </p>
          </div>
        )}

        {(failed || !canCall) && (
          <a
            className="flex items-center justify-center gap-2 rounded-md border bg-white px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-slate-50 max-w-xs mx-auto"
            style={{ color: "#0065A4", borderColor: "rgba(0, 101, 164, 0.3)" }}
            href={MSC_PHONE_HREF}
          >
            <Phone className="w-4 h-4" />
            {MSC_PHONE_DISPLAY}
          </a>
        )}

        {failed && (
          <p className="text-sm text-slate-500 max-w-sm mx-auto">
            We could not call that number. Please call us on the number above and we can help.
          </p>
        )}

        <Button onClick={onReset} variant="ghost" size="sm" className="mx-auto block mt-4 text-slate-400">
          Start Over
        </Button>
      </div>
    </div>
  );
}

/**
 * Placeholder, and still one: the three buttons below log to the console and send nothing. It serves
 * the `existing_appointment` terminal, which nothing in the flow currently reaches.
 *
 * `hh_existing_appointment` no longer uses it — see `ExistingAppointmentNotice` above, which is what
 * this was a sketch of. Whoever implements the remaining channels should follow that one: the
 * appointment never reaches the screen, and the contact detail it is sent to comes off the booking
 * rather than from anything the page holds.
 */
function PrivacyGate({ onReset }: { onReset: () => void }) {
  const [sending, setSending] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const handleSend = async (method: string, fn: () => Promise<void>) => {
    setSending(method);
    await fn();
    setSending(null);
    setSent(method);
  };

  // TODO: Have a notification service that knows household preferred contact methods and then sends email confirmation through that mechanism
  const sendDetailsViaEmail = async () => { console.log("Sending details via email..."); }
  const sendDetailsViaText = async () => { console.log("Sending details via email..."); }
  const sendDetailsViaPhone = async () => { console.log("Sending details via email..."); }

  return (
    <div className="space-y-4 mt-6">
      <div className="bg-amber-50/50 border border-amber-200 rounded-lg p-4 flex items-start gap-3 text-left">
        <Lock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          For your security, appointment details are <strong>not displayed on screen</strong>.
          We can send them to the contact information on&nbsp;file.
        </p>
      </div>

      <div className="grid gap-2 max-w-xs mx-auto">
        {[
          { key: "email", label: "Send via Email", icon: Mail, fn: sendDetailsViaEmail },
          { key: "text", label: "Send via Text", icon: MessageSquare, fn: sendDetailsViaText },
          { key: "phone", label: "Call Me", icon: Phone, fn: sendDetailsViaPhone },
        ].map(({ key, label, icon: Icon, fn }) => (
          <Button
            key={key}
            variant="outline"
            className="w-full justify-start gap-3 h-11"
            disabled={!!sending || sent === key}
            onClick={() => handleSend(key, fn)}
          >
            {sending === key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
            {sent === key ? "Sent ✓" : label}
          </Button>
        ))}
      </div>

      <Button onClick={onReset} variant="ghost" size="sm" className="mx-auto block mt-4 text-slate-400">
        Start Over
      </Button>
    </div>
  );
}

export const EligibilityAlerts = () => {
  const dispatch = useAppDispatch();
  const terminal = useAppSelector(state => state.publicBookingFlow.terminal);
  const { type, detail } = terminal || {};

  // ── Public booking is closed ──
  // Reached when reserve, create or the step-2 household check answers 409 bookingSeasonClosed — the
  // server's backstop for a page that loaded while booking was open, or could not read the season.
  // No Start Over: starting over is refused the same way while booking is closed.
  if (type === BOOKING_SEASON_CLOSED_TERMINAL) {
    return <BookingSeasonClosedNotice message={detail?.message as string | null | undefined} />;
  }

  // ── Household has existing appointment ──
  // Reached when the Social Security number the applicant typed exactly matches one on an
  // appointment that has not happened yet. Terminal: there is nothing to book.
  if (type === "hh_existing_appointment") {
    return (
      <ExistingAppointmentNotice
        noticeId={detail?.noticeId as string | undefined}
        phoneLast4={detail?.phoneLast4 as string | undefined}
        onReset={() => dispatch(flowRestarted())}
      />
    );
  }

  // ── Client has existing appointment ──
  if (type === "existing_appointment") {
    return (
      <div className="w-full max-w-xl mx-auto text-center space-y-4 pt-10 pb-6 px-4">
        <div className="mx-auto w-16 h-16 bg-amber-100 flex items-center justify-center rounded-full">
          <Info className="w-8 h-8 text-amber-600" />
        </div>
        <h2 className="text-2xl font-semibold text-slate-900">Appointment Already Exists</h2>
        <p className="text-slate-600">
          You already have an appointment on file.
        </p>
        <PrivacyGate onReset={() => dispatch(flowRestarted())} />
      </div>
    );
  }

  // ── No funding program remains (M9) ──
  // Reached when the booking is refused with 409 programAlreadyReceived or programDisabled. Nothing
  // was booked; neither Try Again nor another time would change the answer, so only Start Over.
  if (type === PROGRAM_REFUSED_TERMINAL) {
    const message = typeof detail?.message === "string" && detail.message ? detail.message : "This appointment cannot be booked at this time.";
    return (
      <div className="w-full max-w-xl mx-auto text-center space-y-5 pt-10 pb-6 px-4">
        <div className="mx-auto w-16 h-16 bg-amber-100 flex items-center justify-center rounded-full">
          <Info className="w-8 h-8 text-amber-600" />
        </div>
        <h2 className="text-2xl font-semibold text-slate-900">Appointment not available</h2>
        <p className="text-slate-600 whitespace-pre-line">{message}</p>
        <Button onClick={() => dispatch(flowRestarted())} variant="outline">Start Over</Button>
      </div>
    );
  }

  // ── Household has no pledges (AB#590) ──
  // Reached from the step-2 program check: the household behind this exact SSN has already received
  // every program it could be booked for this program year. The hold is released by the page, as for
  // every terminal; the server would refuse the booking anyway (409 programAlreadyReceived).
  // NEEDS HUMAN DECISION (ams-hw9): MSC wording.
  if (type === "hh_no_pledges") {
    const message = typeof detail?.message === "string" && detail.message ? detail.message : null;
    return (
      <div className="w-full max-w-xl mx-auto text-center space-y-5 pt-10 pb-6 px-4">
        <div className="mx-auto w-16 h-16 bg-red-100 flex items-center justify-center rounded-full">
          <AlertTriangle className="w-8 h-8 text-red-600" />
        </div>
        <h2 className="text-2xl font-semibold text-slate-900">No Pledges Available</h2>
        {message ? (
          <p className="text-slate-600 whitespace-pre-line">{message}</p>
        ) : (
          <p className="text-slate-600">
            Your household has exhausted available pledges for the current program year.
            Please apply again next program year.
          </p>
        )}
        <p className="text-slate-600">
          If you still need help with your energy bill this year, call us at{" "}
          <a className="underline" href={MSC_PHONE_HREF}>{MSC_PHONE_DISPLAY}</a>{" "}
          to ask about other assistance programs, or contact your energy provider about payment arrangements.
        </p>
        <Button onClick={() => dispatch(flowRestarted())} variant="outline">Start Over</Button>
      </div>
    );
  }

  // ── Client has no pledges ──
  if (type === "no_pledges") {
    return (
      <div className="w-full max-w-xl mx-auto text-center space-y-5 pt-10 pb-6 px-4">
        <div className="mx-auto w-16 h-16 bg-red-100 flex items-center justify-center rounded-full">
          <AlertTriangle className="w-8 h-8 text-red-600" />
        </div>
        <h2 className="text-2xl font-semibold text-slate-900">No Pledges Available</h2>
        <p className="text-slate-600">
          You have no pledges available for the current program year.
          Please apply again next program year or contact us for assistance.
        </p>
        <Button onClick={() => dispatch(flowRestarted())} variant="outline">Start Over</Button>
      </div>
    );
  }

  // ── Vashon Island service area ──
  if (type === "vashon_island_support") {
    const phone = detail?.phone as string | undefined;
    const extension = detail?.extension as string | undefined;
    const formattedPhone = phone ? formatPhone(phone) : "";
    const telHref = phone ? `tel:${phone}${extension ? `,${extension}` : ""}` : undefined;
    return (
      <div className="w-full max-w-xl mx-auto text-center space-y-5 pt-10 pb-6 px-4">
        <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full" style={{ background: "rgba(0, 101, 164, 0.12)" }}>
          <Phone className="w-8 h-8" style={{ color: "#0065A4" }} />
        </div>
        <h2 className="text-2xl font-semibold text-slate-900">Vashon Island Service Area</h2>
        <p className="text-slate-600">{detail?.message}</p>
        {phone && (
          <a
            className="flex items-center justify-center gap-2 rounded-md border bg-white px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-slate-50 max-w-xs mx-auto"
            style={{ color: "#0065A4", borderColor: "rgba(0, 101, 164, 0.3)" }}
            href={telHref}
          >
            <Phone className="w-4 h-4" />
            {formattedPhone}
            {extension ? ` ext. ${extension}` : ""}
          </a>
        )}
        <Button onClick={() => dispatch(flowRestarted())} variant="outline">Start Over</Button>
      </div>
    );
  }

  // ── Out of area — partner agency ──
  if (type === "out_of_area_agency") {
    const agencyPhone = detail?.phone as string | undefined;
    const formattedAgencyPhone = agencyPhone ? formatPhone(agencyPhone) : "";
    return (
      <div className="w-full max-w-xl mx-auto text-center space-y-5 pt-10 pb-6 px-4">
        <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full" style={{ background: "rgba(0, 101, 164, 0.12)" }}>
          <MapPinOff className="w-8 h-8" style={{ color: "#0065A4" }} />
        </div>
        <h2 className="text-2xl font-semibold text-slate-900">Contact Partner Agency</h2>
        <p className="text-slate-600">You entered a zip code outside MSC’s service area. Please, contact the partner agency that serves your area with the contact information provided below.</p>
        <div className="flex flex-col gap-3 rounded-lg p-4 max-w-xs mx-auto border" style={{ background: "rgba(0, 101, 164, 0.05)", borderColor: "rgba(0, 101, 164, 0.25)" }}>
          <p className="font-semibold" style={{ color: "#004a7a" }}>{detail?.agency}</p>
          {agencyPhone && (
            <a
              className="flex items-center justify-center gap-2 rounded-md border bg-white px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-slate-50"
              style={{ color: "#0065A4", borderColor: "rgba(0, 101, 164, 0.3)" }}
              href={`tel:${agencyPhone}`}
            >
              <Phone className="w-4 h-4" />
              {formattedAgencyPhone}
            </a>
          )}
          <a
            className="flex items-center justify-center gap-2 rounded-md border bg-white px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-slate-50"
            style={{ color: "#0065A4", borderColor: "rgba(0, 101, 164, 0.3)" }}
            href={`${detail?.servicerWebsiteLink}`}
          >
            <Globe className="w-4 h-4" />
            {detail?.servicerWebsiteLink}
          </a>
        </div>
        <Button onClick={() => dispatch(flowRestarted())} variant="outline">Start Over</Button>
      </div>
    );
  }

  // ── Out of area — no agency (211) ──
  if (type === "out_of_area_211") {
    return (
      <div className="w-full max-w-xl mx-auto text-center space-y-5 pt-10 pb-6 px-4">
        <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full" style={{ background: "rgba(0, 101, 164, 0.12)" }}>
          <MapPinOff className="w-8 h-8" style={{ color: "#0065A4" }} />
        </div>
        <h2 className="text-2xl font-semibold text-slate-900">Contact for Other Resources</h2>
        <p className="text-slate-600">{detail?.message}</p>
        <div className="bg-slate-50 border rounded-lg p-4 max-w-xs mx-auto">
          <a href="tel:211">
            <p className="text-slate-900 text-lg font-bold">Call 2-1-1</p>
            <p className="text-xs text-slate-500">Community information line</p>
          </a>
        </div>
        <Button onClick={() => dispatch(flowRestarted())} variant="outline">Start Over</Button>
      </div>
    );
  }

  // ── No appointment slots ──
  if (type === "no_slots") {
    return (
      <div className="w-full max-w-xl mx-auto text-center space-y-5 pt-10 pb-6 px-4">
        <div className="mx-auto w-16 h-16 bg-amber-100 flex items-center justify-center rounded-full">
          <CalendarX2 className="w-8 h-8 text-amber-600" />
        </div>
        <h2 className="text-2xl font-semibold text-slate-900">This Location Fully Booked. Try Again Later.</h2>
        <p className="text-slate-600">{detail?.message || "All appointment slots for the next 28 days are currently filled. Please check back soon."}</p>
        <Button onClick={() => dispatch(flowRestarted())} variant="outline">Start Over</Button>
      </div>
    );
  }

  // ── Somebody else booked the host a moment before this booking was recorded ──
  // Nothing was booked and the hold is gone, so this offers the same "Find another time" as the
  // held-time-gone terminal below (AB#632).
  if (type === SLOT_TAKEN_TERMINAL) {
    return (
      <FindAnotherTimeNotice title="That Time Was Just Taken">
        Someone else booked that appointment time moments before yours could be confirmed, so no
        appointment has been made for you. Your answers are saved, so we can look for another time
        without asking you for them again.
      </FindAnotherTimeNotice>
    );
  }

  // ── The time held for this booking is gone (AB#632) ──
  // The hold expired or was released, its time was booked, or the site changed under it. Never
  // "Try Again": that resubmits the same dead hold.
  if (type === HELD_TIME_GONE_TERMINAL) {
    return (
      <FindAnotherTimeNotice title="That Time Is No Longer Available">
        The appointment time we were holding for you is no longer available, so no appointment has
        been made for you yet. Your answers are saved, so we can look for another time without asking
        you for them again.
      </FindAnotherTimeNotice>
    );
  }

  // ── Slot hold ran out mid-flow ──
  if (type === "reservation_expired") {
    return (
      <div className="w-full max-w-xl mx-auto text-center space-y-5 pt-10 pb-6 px-4">
        <div className="mx-auto w-16 h-16 bg-amber-100 flex items-center justify-center rounded-full">
          <Clock className="w-8 h-8 text-amber-600" />
        </div>
        <h2 className="text-2xl font-semibold text-slate-900">Your Reservation Expired</h2>
        <p className="text-slate-600">
          We held your appointment slot for a limited time and it has now been released so someone
          else can book it. Please start again to get a new appointment slot.
        </p>
        <Button onClick={() => dispatch(flowRestarted())} variant="outline">Start Over</Button>
      </div>
    );
  }

  // ── Booking missing info ──
  if (type === "missing_data") {
    return (
      <div className="w-full max-w-xl mx-auto text-center space-y-5 pt-10 pb-6 px-4">
        <div className="mx-auto w-16 h-16 bg-amber-100 flex items-center justify-center rounded-full">
          <CalendarX2 className="w-8 h-8 text-amber-600" />
        </div>
        <h2 className="text-2xl font-semibold text-slate-900">Missing Information</h2>
        <p className="text-slate-600">{detail?.message || "Please provide all required information to proceed."}</p>
        <Button onClick={() => dispatch(flowRestarted())} variant="outline">Start Over</Button>
      </div>
    );
  }

  // ── Generic error ──
  return (
    <div className="w-full max-w-xl mx-auto text-center space-y-5 pt-10 pb-6 px-4">
      <div className="mx-auto w-16 h-16 bg-red-100 flex items-center justify-center rounded-full">
        <AlertTriangle className="w-8 h-8 text-red-600" />
      </div>
      <h2 className="text-2xl font-semibold text-slate-900">Something Went Wrong</h2>
      <p className="text-slate-600">{detail?.message || "An unexpected error occurred. Please try again."}</p>
      <Button onClick={() => dispatch(flowRestarted())} variant="outline">Start Over</Button>
    </div>
  );
}
